import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Item } from "react-stately";
import { useAsyncList } from "react-stately";
import { MapPinIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";

import { SearchAutocomplete } from "./Form/Autocomplete/SearchAutocomplete";
import { ErrorIcon } from "./Icons/ErrorIcon";
import { Button } from "./Button";

import { iframeMessageHandler } from "~/components/IFrameSetup";
import { Text } from "~/components/Text";
import { useStateManager } from "~/modules/StateManager.hooks";
import {
  type PlaceAutocompleteLoaderData,
  useFetchPlaceAutocomplete,
} from "~/routes/api+/enterprises.$enterprise.place-autocomplete";
import {
  type PlaceDetailLoaderData,
  usePlaceDetailApiFetcher,
} from "~/routes/api+/enterprises.$enterprise.place-details";
import { latLngToGeolocation } from "~/utils/geolocation.utils";

interface LocationSearchProps {
  defaultPlaceId?: string | null;
  onSelectionChange: (item: PlaceDetailLoaderData) => void;
  label: string;
  description?: ReactNode;
  placeholder?: string;
  isDisabled?: boolean;
}

export function LocationSearch({
  defaultPlaceId,
  description,
  isDisabled,
  label,
  onSelectionChange,
  placeholder,
}: LocationSearchProps) {
  const { t } = useTranslation();

  const {
    error,
    list,
    loadingState,
    onCurrentPositionRequested,
    onInputChange,
    onPlaceSelected,
    selectedKey,
  } = useLocationSearch({ placeId: defaultPlaceId, onSelectionChange });

  return (
    <SearchAutocomplete
      addonRight={
        <Button
          className="flex cursor-pointer items-center p-3xs text-sm text-secondary"
          data-component="CurrentLocationButton"
          onPress={() => onCurrentPositionRequested()}
        >
          <LocationSearchAddon error={error} />
        </Button>
      }
      data-component="LocationSearch"
      description={description}
      inputValue={list.filterText}
      isDisabled={isDisabled}
      items={list.items}
      label={label}
      loadingState={loadingState}
      onInputChange={onInputChange}
      onSelectionChange={onPlaceSelected}
      placeholder={placeholder ?? t("LocationSearch.placeholder")}
      selectedKey={selectedKey}
    >
      {(result) => <Item key={result.place_id}>{result.description}</Item>}
    </SearchAutocomplete>
  );
}

function LocationSearchAddon({ error }: { error: boolean }) {
  if (error) {
    return (
      <>
        <span className={clsx("text-error-700", "mr-xs")}>
          <ErrorIcon />
        </span>
        <span className="text-error-700">
          <Text i18nKey="LocationSearch.error" />
        </span>
      </>
    );
  }

  return (
    <>
      <MapPinIcon className={clsx("h-sm w-sm", "mr-xs")} />
      <span>
        <Text i18nKey="LocationSearch.query" />
      </span>
    </>
  );
}

interface UseLocationSearchArgs {
  placeId?: string | null;
  onSelectionChange: (item: PlaceDetailLoaderData) => void;
}

function useLocationSearch({ onSelectionChange, placeId }: UseLocationSearchArgs) {
  const {
    state: {
      config: { searchCountries },
    },
  } = useStateManager();

  const {
    data: placeDetails,
    isLoading: isPlaceDetailsLoading,
    loadPlaceDetails,
    sessionToken,
  } = usePlaceDetailsFetcher({ onSelectionChange });

  const fetchAutoComplete = useFetchPlaceAutocomplete(sessionToken);

  const [userLocationState, setUserLocationState] = useState<
    "pending" | "success" | "error" | "idle"
  >("idle");

  const list = useAsyncList<ArrayElement<PlaceAutocompleteLoaderData>>({
    getKey(item) {
      return item.place_id;
    },
    async load({ filterText, signal }) {
      if (!filterText) {
        return { items: [] };
      }

      const items = await fetchAutoComplete({
        input: filterText,
        countries: searchCountries,
        signal,
      });

      return { items };
    },
  });

  useEffect(() => {
    if (placeId) {
      loadPlaceDetails({ placeId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!placeDetails?.placeId) return;

    if (!list.getItem(placeDetails.placeId)) {
      list.prepend({
        description: placeDetails.formattedAddress,
        place_id: placeDetails.placeId,
      });
      list.setFilterText(placeDetails.formattedAddress);
    }

    list.setSelectedKeys(new Set([placeDetails.placeId]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeDetails?.placeId, placeDetails?.formattedAddress]);

  let onInputChange = (value: string) => {
    // Clear key if user deletes all text in the field
    if (value === "") {
      list.setSelectedKeys(new Set([]));
      loadPlaceDetails({ placeId: null });
    }

    list.setFilterText(value);
  };

  const onPlaceSelected = (placeId: React.Key) => {
    if (placeId) {
      const itemText = list.getItem(placeId)?.description;
      const placeText = placeDetails?.placeId === placeId ? placeDetails.formattedAddress : "";
      onInputChange(itemText || placeText);
    }

    list.setSelectedKeys(new Set([placeId]));
    loadPlaceDetails({ placeId: placeId?.toString() });
  };

  const onCurrentPositionRequested = async () => {
    if (userLocationState === "pending") return;

    setUserLocationState("pending");
    try {
      const { coords } = await iframeMessageHandler.getCurrentPosition();
      setUserLocationState("success");

      const geolocation = latLngToGeolocation({ lat: coords.latitude, lng: coords.longitude });
      loadPlaceDetails({ geolocation });
    } catch (err) {
      console.error(err);
      setUserLocationState("error");
    }
  };

  const selectedKey =
    list.selectedKeys === "all" ? null : list.selectedKeys.values().next().value ?? null;

  const loadingState = isPlaceDetailsLoading
    ? "loading"
    : userLocationState === "pending"
    ? "loading"
    : list.loadingState;

  return {
    list,
    loadingState,
    onCurrentPositionRequested,
    onInputChange,
    onPlaceSelected,
    selectedKey,
    error: userLocationState === "error",
  };
}

function usePlaceDetailsFetcher({
  onSelectionChange,
}: Pick<UseLocationSearchArgs, "onSelectionChange">) {
  const fetcher = usePlaceDetailApiFetcher();

  const ssrToken = useId();
  const [sessionToken, setSessionToken] = useState(ssrToken);

  useEffect(() => {
    setSessionToken(uuidv4());
  }, [fetcher.data?.placeId]);

  let onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  useEffect(() => {
    fetcher.data && onSelectionChangeRef.current(fetcher.data);
  }, [fetcher.data]);

  const loadPlaceDetails = (
    params:
      | { geolocation?: never; placeId: string | null }
      | { geolocation: string; placeId?: never },
  ) => {
    if (params.placeId === null) {
      return onSelectionChangeRef.current(null);
    }

    if (params.placeId && fetcher.data && params.placeId === fetcher.data.placeId) {
      return onSelectionChangeRef.current(fetcher.data);
    }

    const searchParams = new URLSearchParams();
    if (params.placeId) {
      searchParams.set("placeId", params.placeId);
      searchParams.set("sessionToken", sessionToken);
    } else {
      params.geolocation && searchParams.set("geolocation", params.geolocation);
    }

    fetcher.submit(searchParams.toString());
  };

  return { ...fetcher, loadPlaceDetails, sessionToken };
}
